/**
 * Toast Service
 * Servizio centralizzato per mostrare notifiche toast
 * Gestisce automaticamente i messaggi di errore da ApiClientError
 */
import type React from "react";
import { toast as sonnerToast } from "sonner";
import { ApiClientError } from "./api-client";

export interface ToastOptions {
    duration?: number;
    description?: string;
    action?: {
        label: string;
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    };
    cancel?: {
        label: string;
        onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    };
}

/**
 * Estrae il messaggio di errore da un errore API o generico
 */
function extractErrorMessage(error: unknown): string {
    if (error instanceof ApiClientError) {
        return error.detail || `Errore ${error.status}: Richiesta fallita`;
    }

    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === "string") {
        return error;
    }

    return "Si è verificato un errore imprevisto";
}

/**
 * Servizio toast con funzioni helper per diversi tipi di notifiche
 */
export const toast = {
    /**
     * Mostra un toast di successo
     */
    success: (message: string, options?: ToastOptions) => {
        return sonnerToast.success(message, {
            duration: options?.duration ?? 5000,
            description: options?.description,
            action: options?.action,
            cancel: options?.cancel
                ? {
                      label: options.cancel.label,
                      onClick: options.cancel.onClick || (() => {}),
                  }
                : undefined,
        });
    },

    /**
     * Mostra un toast di errore
     * Accetta stringa, Error o ApiClientError
     */
    error: (error: string | Error | unknown, options?: ToastOptions) => {
        const message = typeof error === "string" ? error : extractErrorMessage(error);
        return sonnerToast.error(message, {
            duration: options?.duration ?? 7000,
            description: options?.description,
            action: options?.action,
            cancel: options?.cancel
                ? {
                      label: options.cancel.label,
                      onClick: options.cancel.onClick || (() => {}),
                  }
                : undefined,
        });
    },

    /**
     * Mostra un toast di warning
     */
    warning: (message: string, options?: ToastOptions) => {
        return sonnerToast.warning(message, {
            duration: options?.duration ?? 6000,
            description: options?.description,
            action: options?.action,
            cancel: options?.cancel
                ? {
                      label: options.cancel.label,
                      onClick: options.cancel.onClick || (() => {}),
                  }
                : undefined,
        });
    },

    /**
     * Mostra un toast informativo
     */
    info: (message: string, options?: ToastOptions) => {
        return sonnerToast.info(message, {
            duration: options?.duration ?? 5000,
            description: options?.description,
            action: options?.action,
            cancel: options?.cancel
                ? {
                      label: options.cancel.label,
                      onClick: options.cancel.onClick || (() => {}),
                  }
                : undefined,
        });
    },

    /**
     * Mostra un toast di caricamento
     */
    loading: (message: string, options?: Omit<ToastOptions, "action" | "cancel">) => {
        return sonnerToast.loading(message, {
            duration: options?.duration,
            description: options?.description,
        });
    },

    /**
     * Promessa che mostra un toast di caricamento e poi successo/errore
     */
    promise: <T>(
        promise: Promise<T>,
        {
            loading,
            success,
            error,
        }: {
            loading: string;
            success: string | ((data: T) => string);
            error: string | ((error: unknown) => string);
        },
    ) => {
        return sonnerToast.promise(promise, {
            loading,
            success: (data: T) => (typeof success === "function" ? success(data) : success),
            error: (err: unknown) => (typeof error === "function" ? error(err) : error),
        });
    },

    /**
     * Chiude un toast specifico
     */
    dismiss: (toastId?: string | number) => {
        sonnerToast.dismiss(toastId);
    },

    /**
     * Chiude tutti i toast
     */
    dismissAll: () => {
        // Sonner doesn't have dismissAll, so we dismiss with undefined to clear all
        sonnerToast.dismiss();
    },
};
